import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookmarkPlus, LayoutTemplate, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { SI, useSI } from "@/lib/sourcing";

// §105 Reusable search templates built from AI-assisted searches.
const EP = `${SI}/search-templates`;

export const SaveTemplateButton = ({ plan, name }) => {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post(EP, { name: v.trim(), params: plan.params || {}, boolean_query: plan.boolean_query || "", alt_titles: plan.alt_titles || [], skills: plan.skills || [] }); toast.success("Template saved. Find it above the search form."); window.dispatchEvent(new Event("si-templates")); setOpen(false); }
    catch (e) { toast.error(e?.response?.status === 404 ? "Search templates aren't live on the server yet (spec §105)." : e?.response?.data?.detail || "Couldn't save"); } finally { setBusy(false); }
  };
  return <>
    <button type="button" onClick={() => { setV(name || ""); setOpen(true); }} className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[#8224e3]/50 px-3 text-xs text-[#c79bff] transition-colors hover:bg-[#8224e3]/10" data-testid="qs-tpl-save"><BookmarkPlus className="h-3.5 w-3.5" />Save as template</button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="border-[#27272A] bg-[#0f0f12]" data-testid="qs-tpl-dialog">
        <DialogTitle className="font-display text-zinc-50">Save as a search template</DialogTitle>
        <p className="text-xs text-zinc-400">Reuse this search for similar roles. Your team will see it too.</p>
        <Input value={v} onChange={(e) => setV(e.target.value)} placeholder="e.g. Senior backend — fintech" className="h-9" data-testid="qs-tpl-name" />
        <Button onClick={save} disabled={busy || !v.trim()} className="bg-[#8224e3] text-white hover:bg-[#9a45f0]" data-testid="qs-tpl-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save template</Button>
      </DialogContent>
    </Dialog>
  </>;
};

export const SearchTemplates = ({ onApply }) => {
  const q = useSI(EP);
  const { reload } = q;
  const [del, setDel] = useState(null);
  useEffect(() => { const f = () => reload?.(); window.addEventListener("si-templates", f); return () => window.removeEventListener("si-templates", f); }, [reload]);
  const items = q.data?.items || [];
  const remove = async () => { try { await api.delete(`${EP}/${del.id}`); setDel(null); q.reload(); } catch { toast.error("Couldn't delete"); } };
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="qs-templates">
      <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500"><LayoutTemplate className="h-3.5 w-3.5 text-[#c79bff]" />Templates</span>
      {items.map((t) => <span key={t.id} className="group inline-flex items-center rounded-full border border-[#8224e3]/40 bg-[#8224e3]/5 text-[11px] text-[#c79bff]">
        <button type="button" onClick={() => { onApply({ params: t.params, boolean_query: t.boolean_query, alt_titles: t.alt_titles, skills: t.skills }); api.post(`${EP}/${t.id}/use`).catch(() => {}); }} title={[t.params?.title, ...(t.alt_titles || [])].filter(Boolean).join(", ")} className="px-2.5 py-1 hover:text-white" data-testid={`qs-tpl-${t.id}`}>{t.name}</button>
        <button type="button" onClick={() => setDel(t)} className="pr-2 text-zinc-500 opacity-60 hover:text-red-300 group-hover:opacity-100" data-testid={`qs-tpl-del-${t.id}`}><X className="h-3 w-3" /></button></span>)}
      <Dialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <DialogContent className="border-[#27272A] bg-[#0f0f12]" data-testid="qs-tpl-del-dialog">
          <DialogTitle className="font-display text-zinc-50">Delete "{del?.name}"?</DialogTitle>
          <p className="text-xs text-zinc-400">This removes the template for your whole team.</p>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setDel(null)} className="border-[#27272A]">Cancel</Button><Button onClick={remove} className="bg-red-500 text-white hover:bg-red-400" data-testid="qs-tpl-del-confirm">Delete</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
